import {
  Avatar,
  Badge,
  Button,
  ChannelIcon,
  channelMeta,
  appointmentStatusMeta,
  ConversationRow,
  EmptyState,
  emptyStates,
  formatMessageAge,
  formatPrice,
  formatTime,
  type CommunicationChannel,
} from "@coucou/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { AppointmentForm, type EditableAppointment } from "../components/appointment-form";
import { SALON_ID, SALON_TIMEZONE as TZ, api } from "../lib/trpc";

type Filter = "all" | "unread" | "mine" | "unassigned";

/**
 * La forme du payload est déduite du contrat tRPC, pas importée de `@coucou/db` :
 * le poste du salon n'a aucune raison de dépendre du paquet base de données
 * (il embarquerait Drizzle et le pilote PostgreSQL dans le bundle). C'est
 * exactement ce que l'`AppRouter` partagé est censé éviter.
 */
type ThreadMessage = Awaited<ReturnType<typeof api.inbox.thread.query>>["messages"][number];
type MessagePayload = ThreadMessage["payload"];

/**
 * Le hub de communication — trois colonnes.
 *
 * Gauche : les fils, triés par activité. Centre : la discussion. Droite : le
 * CONTEXTE de la cliente.
 *
 * La colonne de droite est le vrai différenciateur : répondre « oui, jeudi
 * 14 h » sans avoir l'historique de RDV sous les yeux, c'est répondre à
 * l'aveugle. C'est ce qui distingue ce hub d'un simple client de messagerie.
 */
export function Inbox() {
  const [filter, setFilter] = useState<Filter>("all");
  /** Filtre de canal, independant du filtre d'etat. `""` = tous. */
  const [canal, setCanal] = useState<"" | CommunicationChannel>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  /** Modèle ayant servi de base au brouillon : sert au comptage d'usage à l'envoi. */
  const [usedTemplateId, setUsedTemplateId] = useState<string | null>(null);
  const [creatingAppointment, setCreatingAppointment] = useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /*
   * `useInfiniteQuery` et non `useQuery` : la liste s'arretait aux 25 premiers
   * fils, sans bouton pour aller plus loin. Le serveur renvoyait pourtant un
   * `nextCursor` que personne ne lisait.
   *
   * Invisible tant que le salon avait dix conversations. Des que les avis
   * Google sont arrives, cinquante fils d'un coup, la moitie est devenue
   * inatteignable — un avis de l'an dernier auquel on venait de repondre
   * n'existait plus nulle part dans l'ecran.
   */
  const list = useInfiniteQuery({
    queryKey: ["inbox", filter, canal],
    queryFn: ({ pageParam }) =>
      api.inbox.list.query({
        salonId: SALON_ID,
        filter,
        ...(canal ? { channel: canal } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (derniere) => derniere.nextCursor,
    refetchInterval: 30_000,
  });

  const thread = useQuery({
    queryKey: ["thread", selectedId],
    queryFn: () => api.inbox.thread.query({ conversationId: selectedId! }),
    enabled: Boolean(selectedId),
  });

  /** Réponses types, triées côté serveur par usage : les plus utiles remontent. */
  const templates = useQuery({
    queryKey: ["inbox", "templates"],
    queryFn: () => api.inbox.templates.query({ salonId: SALON_ID }),
  });

  /** Chargée seulement à l'ouverture du formulaire de RDV. */
  const team = useQuery({
    queryKey: ["admin", "team"],
    queryFn: () => api.admin.team.list.query({ salonId: SALON_ID }),
    enabled: creatingAppointment || editingAppointmentId != null,
  });

  /** « Elle a dit oui » — le créneau proposé devient un vrai rendez-vous. */
  const confirmAppointment = useMutation({
    mutationFn: (appointmentId: string) => api.agenda.confirm.mutate({ appointmentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread", selectedId] });
      queryClient.invalidateQueries({ queryKey: ["pulse"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const markRead = useMutation({
    mutationFn: (conversationId: string) => api.inbox.markRead.mutate({ conversationId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["inbox"] }),
  });

  /** Rapprochement d'un contact anonyme avec une fiche cliente existante. */
  const linkClient = useMutation({
    mutationFn: (userId: string) =>
      api.inbox.linkIdentity.mutate({ identityId: detail!.conversation.primaryIdentityId!, userId }),
    onSuccess: () => {
      setClientSearch("");
      queryClient.invalidateQueries({ queryKey: ["thread", selectedId] });
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
  });

  const candidates = useQuery({
    queryKey: ["clientSearch", clientSearch],
    queryFn: () => api.agenda.searchClients.query({ salonId: SALON_ID, query: clientSearch }),
    enabled: clientSearch.trim().length >= 2,
  });

  const reply = useMutation({
    mutationFn: ({ text, templateId }: { text: string; templateId?: string }) =>
      api.inbox.reply.mutate({ conversationId: selectedId!, text, templateId }),
    onSuccess: () => {
      setDraft("");
      setUsedTemplateId(null);
      queryClient.invalidateQueries({ queryKey: ["thread", selectedId] });
      queryClient.invalidateQueries({ queryKey: ["inbox"] });
      queryClient.invalidateQueries({ queryKey: ["inbox", "templates"] });
    },
  });

  const conversations = list.data?.pages.flatMap((page) => page.items) ?? [];

  /*
   * Navigation au clavier, fleches haut et bas.
   *
   * Les valeurs courantes passent par une REF, et l'ecouteur ne s'installe
   * qu'une fois. Les mettre dans les dependances aurait desinscrit et
   * reinscrit l'ecouteur a chaque rendu — `conversations` est un tableau
   * neuf a chaque fois, issu d'un `flatMap` — et ce va-et-vient a deja coute
   * cher ailleurs dans ce projet, sur le glisser-deposer du planning.
   */
  const navigation = useRef({
    conversations,
    selectedId,
    hasNextPage: list.hasNextPage,
    fetchNextPage: list.fetchNextPage,
  });
  navigation.current = {
    conversations,
    selectedId,
    hasNextPage: list.hasNextPage,
    fetchNextPage: list.fetchNextPage,
  };

  useEffect(() => {
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== "ArrowDown" && evenement.key !== "ArrowUp") return;

      /*
       * On ne vole JAMAIS les fleches a un champ de saisie.
       *
       * La zone de reponse occupe la moitie de l'ecran : sans ce garde-fou,
       * deplacer le curseur dans un brouillon de trois lignes changerait de
       * conversation et ferait perdre le texte en cours.
       */
      const cible = evenement.target as HTMLElement | null;
      if (
        cible &&
        (cible.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(cible.tagName))
      ) {
        return;
      }

      const { conversations: fils, selectedId: courant, hasNextPage, fetchNextPage } =
        navigation.current;
      if (fils.length === 0) return;

      evenement.preventDefault();

      const index = fils.findIndex((fil) => fil.id === courant);
      // Rien de selectionne : la premiere fleche ouvre le fil du haut.
      if (index === -1) {
        setSelectedId(fils[0]!.id);
        return;
      }

      const suivant = evenement.key === "ArrowDown" ? index + 1 : index - 1;
      if (suivant < 0) return;

      if (suivant >= fils.length) {
        // Au bout de la liste, on va chercher la page suivante plutot que de
        // buter : la selection suivra au prochain rendu.
        if (hasNextPage) fetchNextPage();
        return;
      }

      setSelectedId(fils[suivant]!.id);
    };

    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, []);

  /* La ligne choisie au clavier doit rester visible sans avoir a la chercher. */
  useEffect(() => {
    if (!selectedId) return;
    document
      .getElementById(`fil-${selectedId}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  // Ouvrir un fil le marque comme lu — sans bouton dédié, comme partout ailleurs.
  // Le brouillon repart à zéro : une réponse type préparée pour Léa ne doit pas
  // se retrouver dans la conversation de Camille.
  useEffect(() => {
    if (selectedId) markRead.mutate(selectedId);
    setDraft("");
    setUsedTemplateId(null);
    setClientSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const detail = thread.data;

  return (
    <div className="flex h-full min-h-0">
      {/* --- Colonne 1 : les fils ---------------------------------------- */}
      <div className="flex w-80 shrink-0 flex-col border-r border-creme-200">
        <div className="flex gap-1 p-3">
          {(
            [
              ["all", "Tout"],
              ["unread", "Non lus"],
              ["mine", "À moi"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={[
                "rounded-full px-3 py-1.5 text-sm transition-colors",
                filter === key
                  ? "bg-blush-500 text-white"
                  : "text-encre-600 hover:bg-creme-200",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>

        {/*
          Une liste deroulante et non des pastilles : six canaux ne tiennent
          pas sur une colonne de 320 points, et la rangee du dessus est deja
          pleine. Le libelle indique le canal courant sans rien couper.
        */}
        <div className="px-3 pb-3">
          <select
            value={canal}
            onChange={(event) => setCanal(event.target.value as "" | CommunicationChannel)}
            className="h-9 w-full rounded-lg border border-creme-300 bg-white px-2 text-sm text-encre-700"
          >
            <option value="">Tous les canaux</option>
            <option value="instagram">Instagram</option>
            <option value="google_review">Avis Google</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">E-mail</option>
            <option value="internal_note">Notes internes</option>
          </select>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
          {list.isPending && <p className="p-3 text-sm text-encre-400">Chargement…</p>}

          {list.data && conversations.length === 0 && (
            <EmptyState {...emptyStates.inboxZero} />
          )}

          {conversations.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              id={`fil-${conversation.id}`}
              client={conversation.client}
              fallbackName={
                conversation.primaryIdentity?.displayName ??
                conversation.primaryIdentity?.handle ??
                "Contact non rapproché"
              }
              channel={conversation.lastChannel as CommunicationChannel}
              preview={conversation.lastMessagePreview}
              lastMessageAt={conversation.lastMessageAt}
              unreadCount={conversation.unreadCount}
              direction={conversation.lastMessageDirection}
              assigneeName={conversation.assignee?.firstName}
              respondByAt={conversation.respondByAt}
              selected={conversation.id === selectedId}
              onClick={() => setSelectedId(conversation.id)}
            />
          ))}

          {/*
            Chargement a la demande plutot qu'au defilement : sur une colonne
            de fils, un declenchement automatique fait bouger la liste sous le
            curseur au moment ou l'on vise une conversation.
          */}
          {list.hasNextPage && (
            <button
              type="button"
              onClick={() => list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
              className="mt-2 w-full rounded-lg border border-creme-300 px-3 py-2 text-sm text-encre-600 transition-colors hover:bg-creme-100 disabled:opacity-60"
            >
              {list.isFetchingNextPage ? "Chargement…" : "Afficher les conversations plus anciennes"}
            </button>
          )}
        </div>
      </div>

      {/* --- Colonne 2 : la discussion ----------------------------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        {!selectedId && (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState
              glyph="💬"
              title="Choisissez une conversation"
              description="Tous les canaux arrivent ici : Instagram, WhatsApp, SMS."
            />
          </div>
        )}

        {detail && (
          <>
            <header className="flex items-center gap-3 border-b border-creme-200 px-5 py-3">
              <Avatar
                src={detail.conversation.client?.avatarUrl}
                // Le nom du canal a defaut du compte : un « ? » dans l'avatar
                // alors que la liste affiche « Camille B. » est incoherent.
                firstName={detail.conversation.client?.firstName ?? contactName(detail)}
                lastName={detail.conversation.client?.lastName}
              />
              <div className="min-w-0">
                <p className="truncate font-display text-lg leading-tight">
                  {detail.conversation.client
                    ? `${detail.conversation.client.firstName} ${detail.conversation.client.lastName ?? ""}`
                    : contactName(detail)}
                </p>
                <p className="flex items-center gap-1.5 text-xs text-encre-400">
                  <ChannelIcon channel={detail.conversation.lastChannel as CommunicationChannel} />
                  {/* Libelles metier : « whatsapp » brut ne doit jamais fuir. */}
                  {detail.identities
                    .map((identity) => channelMeta[identity.channel as CommunicationChannel].label)
                    .join(" · ") || "—"}
                </p>
              </div>
            </header>

            <div className="min-h-0 flex-1 space-y-2 overflow-auto px-5 py-4">
              {detail.messages.map((message) => {
                const outbound = message.direction === "outbound";

                return (
                  <div
                    key={message.id}
                    className={outbound ? "flex justify-end" : "flex justify-start"}
                  >
                    <div
                      className={[
                        "max-w-[70%] rounded-xl px-3.5 py-2 text-sm animate-fade-up",
                        message.channel === "internal_note"
                          ? "bg-ambre-100 text-ambre-700 italic"
                          : outbound
                            ? "bg-blush-500 text-white"
                            // creme-100 sur fond blanc etait quasi invisible :
                            // les bulles entrantes se confondaient avec le fond.
                            : "bg-creme-200 text-encre-800",
                      ].join(" ")}
                    >
                      <MessageBody payload={message.payload} outbound={outbound} />
                      <p
                        className={[
                          "mt-1 text-[10px]",
                          outbound ? "text-white/70" : "text-encre-400",
                        ].join(" ")}
                      >
                        {formatMessageAge(message.sentAt)}
                        {outbound && message.deliveryStatus === "queued" && " · en attente"}
                      </p>

                      {/*
                        Un envoi qui échoue DOIT se voir. Sans ce bandeau, la
                        bulle d'un message jamais parti est identique à celle
                        d'un message remis : l'équipe répond « oui, j'ai de la
                        place samedi », voit sa phrase dans le fil, et la
                        cliente n'a jamais rien reçu. Le motif est affiché tel
                        quel — « jeton absent » et « numéro invalide » n'appellent
                        pas la même action.
                      */}
                      {outbound && message.deliveryStatus === "failed" && (
                        <p className="mt-1 rounded bg-grenat-700/90 px-1.5 py-0.5 text-[10px] text-white">
                          ⚠ Non remis{message.failureReason ? ` — ${message.failureReason}` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <form
              className="border-t border-creme-200 p-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (draft.trim() && !hasPlaceholder(draft)) {
                  reply.mutate({
                    text: draft.trim(),
                    templateId: usedTemplateId ?? undefined,
                  });
                }
              }}
            >
              {/*
                Réponses types. Un clic REMPLIT la zone de saisie, il n'envoie
                jamais.
                Un modèle est une phrase générique : « il me reste de la place »
                doit être confronté au planning, un tarif à la prestation
                réellement demandée. Le faire partir en un clic, c'est répondre
                à la place de l'équipe sur des informations qu'elle seule peut
                vérifier — et une cliente à qui on promet un créneau inexistant
                coûte plus cher que trois secondes de relecture.
              */}
              {detail.canReplyFreely && (templates.data?.length ?? 0) > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {templates.data!.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      title={fillTemplate(template.body, detail.conversation.client?.firstName)}
                      onClick={() => {
                        setDraft(
                          fillTemplate(template.body, detail.conversation.client?.firstName),
                        );
                        setUsedTemplateId(template.id);
                      }}
                      className="rounded-full border border-creme-300 bg-creme-100 px-3 py-1 text-xs text-encre-600 transition-colors hover:border-blush-300 hover:bg-blush-50"
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Variable laissée en place : l'envoi est bloqué tant qu'elle
                  n'est pas remplacée. « Je vous propose {{creneau}} » parti
                  tel quel, c'est le salon qui passe pour négligent. */}
              {hasPlaceholder(draft) && (
                <p className="mb-2 rounded-md bg-ambre-100 px-3 py-1.5 text-xs text-ambre-700">
                  Complétez les passages entre accolades avant d'envoyer.
                </p>
              )}

              {/* Fenêtre Meta expirée : on grise plutôt que d'échouer à l'envoi. */}
              {!detail.canReplyFreely && (
                <p className="mb-2 rounded-md bg-ambre-100 px-3 py-1.5 text-xs text-ambre-700">
                  La fenêtre de réponse de 24 h est fermée. Seul un modèle validé peut partir.
                </p>
              )}
              <div className="flex gap-2">
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={!detail.canReplyFreely}
                  placeholder="Votre réponse…"
                  className="h-11 min-w-0 flex-1 rounded-full border border-creme-300 px-4 outline-none focus:border-blush-400 focus:ring-2 focus:ring-blush-200 disabled:bg-creme-100"
                />
                <Button
                  type="submit"
                  loading={reply.isPending}
                  disabled={!draft.trim() || !detail.canReplyFreely || hasPlaceholder(draft)}
                >
                  Envoyer
                </Button>
              </div>
            </form>
          </>
        )}
      </div>

      {/* --- Colonne 3 : le contexte cliente ------------------------------ */}
      {detail && (
        <aside className="w-72 shrink-0 overflow-auto border-l border-creme-200 p-4">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-encre-400">
            Contexte
          </h2>

          {/*
            Rapprochement. Une cliente qui écrit en DM n'est qu'un identifiant
            Instagram tant qu'on ne l'a pas reliée à sa fiche : ni historique,
            ni allergies, ni rendez-vous. C'est le geste qui donne au hub tout
            son intérêt, il doit tenir dans la colonne où le manque se voit.
          */}
          {!detail.conversation.client && detail.conversation.primaryIdentityId && (
            <div className="mb-4 rounded-lg bg-ambre-100 p-3">
              <p className="mb-2 text-xs text-ambre-700">
                Contact non rapproché. Reliez-le à une fiche cliente pour voir son
                historique.
              </p>
              <input
                value={clientSearch}
                onChange={(event) => setClientSearch(event.target.value)}
                placeholder="Nom ou téléphone…"
                className="h-9 w-full rounded-md border border-creme-300 px-2.5 text-sm outline-none focus:border-blush-400"
              />

              {clientSearch.trim().length >= 2 && (candidates.data?.length ?? 0) === 0 && (
                <p className="mt-2 text-xs text-encre-500">Aucune cliente à ce nom.</p>
              )}

              <ul className="mt-2 space-y-1">
                {(candidates.data ?? []).map((candidate) => (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      disabled={linkClient.isPending}
                      onClick={() => linkClient.mutate(candidate.id)}
                      className="w-full rounded-md bg-white px-2.5 py-1.5 text-left text-sm hover:bg-blush-50 disabled:opacity-50"
                    >
                      <span className="font-medium text-encre-800">
                        {candidate.firstName} {candidate.lastName ?? ""}
                      </span>
                      <span className="block text-xs text-encre-500">
                        {candidate.phone ?? "sans téléphone"}
                        {candidate.visitCount != null && ` · ${candidate.visitCount} visite(s)`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prendre le rendez-vous sans quitter la conversation ni ressaisir
              la cliente : c'est la fin normale d'un échange « vous avez de la
              place samedi ? ». */}
          <Button
            variant="secondary"
            className="mb-4 w-full"
            onClick={() => setCreatingAppointment(true)}
          >
            Créer un rendez-vous
          </Button>

          {detail.conversation.client?.clientProfile && (
            <dl className="mb-4 space-y-1 text-sm">
              <Row label="Visites" value={String(detail.conversation.client.clientProfile.visitCount)} />
              <Row
                label="Dernière"
                value={
                  detail.conversation.client.clientProfile.lastVisitAt
                    ? new Intl.DateTimeFormat("fr-FR", { timeZone: TZ, dateStyle: "medium" }).format(
                        detail.conversation.client.clientProfile.lastVisitAt,
                      )
                    : "—"
                }
              />
              <Row
                label="Total dépensé"
                value={formatPrice(detail.conversation.client.clientProfile.lifetimeValueCents)}
              />
              {detail.conversation.client.clientProfile.noShowCount > 0 && (
                <Row
                  label="Absences"
                  value={String(detail.conversation.client.clientProfile.noShowCount)}
                  alert
                />
              )}
            </dl>
          )}

          <h3 className="mb-2 text-sm font-medium text-encre-600">Rendez-vous</h3>
          {detail.appointments.length === 0 ? (
            <p className="text-sm text-encre-400">Aucun rendez-vous.</p>
          ) : (
            <ul className="space-y-2">
              {detail.appointments.map((appointment) => {
                const proposed = appointment.status === "requested";
                return (
                  <li
                    key={appointment.id}
                    className={[
                      "rounded-lg p-2.5 text-sm",
                      // Un créneau proposé mais pas encore accepté ne doit pas
                      // se lire comme un rendez-vous acquis.
                      proposed ? "bg-ambre-100 ring-1 ring-ambre-200" : "bg-creme-100",
                    ].join(" ")}
                  >
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="font-medium tabular-nums text-encre-800">
                        {new Intl.DateTimeFormat("fr-FR", {
                          timeZone: TZ,
                          dateStyle: "medium",
                        }).format(appointment.startsAt)}{" "}
                        · {formatTime(appointment.startsAt, TZ)}
                      </p>
                      {proposed && (
                        <Badge tone={appointmentStatusMeta.requested.tone}>
                          {appointmentStatusMeta.requested.label}
                        </Badge>
                      )}
                    </div>
                    <p className="text-encre-500">
                      {appointment.lines.map((line) => line.nameSnapshot).join(" + ")}
                    </p>

                    {/* La cliente a dit oui : un tap, et le créneau est acquis.
                        Modifier rouvre le formulaire habituel. */}
                    {proposed && (
                      <div className="mt-2 flex gap-1.5">
                        <Button
                          size="sm"
                          loading={confirmAppointment.isPending}
                          onClick={() => confirmAppointment.mutate(appointment.id)}
                        >
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingAppointmentId(appointment.id)}
                        >
                          Modifier
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      )}

      {/* Modification d'un créneau proposé : même formulaire que le planning,
          reconstruit depuis ce que le fil connaît déjà de la cliente. */}
      {editingAppointmentId && detail && team.data && (
        <AppointmentForm
          salonId={SALON_ID}
          staff={staffOptions(team.data)}
          defaultStartsAt={nextRoundSlot()}
          appointment={editableFrom(detail, editingAppointmentId)}
          onClose={() => {
            setEditingAppointmentId(null);
            queryClient.invalidateQueries({ queryKey: ["thread", selectedId] });
          }}
        />
      )}

      {creatingAppointment && detail && team.data && (
        <AppointmentForm
          salonId={SALON_ID}
          staff={staffOptions(team.data)}
          defaultStartsAt={nextRoundSlot()}
          conversationId={detail.conversation.id}
          defaultClient={
            detail.conversation.client
              ? {
                  id: detail.conversation.client.id,
                  firstName: detail.conversation.client.firstName,
                  lastName: detail.conversation.client.lastName,
                  phone: detail.conversation.client.phone,
                  allergies: detail.conversation.client.clientProfile?.allergies ?? null,
                }
              : undefined
          }
          onClose={() => {
            setCreatingAppointment(false);
            queryClient.invalidateQueries({ queryKey: ["thread", selectedId] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Interpolation des variables d'un modèle. On ne résout que ce dont on est
 * SÛR : le prénom quand la cliente est rapprochée. `{{creneau}}` reste ouvert,
 * c'est à l'équipe de le remplir — proposer une heure inventée serait pire
 * que de ne rien proposer.
 */
function fillTemplate(body: string, firstName?: string | null): string {
  return firstName ? body.replaceAll("{{prenom}}", firstName) : body;
}

function hasPlaceholder(body: string): boolean {
  return /\{\{[^}]+\}\}/.test(body);
}

/** Prochaine demi-heure pleine : un RDV posé à 14h07 n'a aucun sens. */
function nextRoundSlot(): Date {
  const slot = new Date();
  slot.setSeconds(0, 0);
  slot.setMinutes(slot.getMinutes() > 30 ? 60 : 30);
  return slot;
}

/** Nom affichable d'un contact non encore rapproche a une cliente. */
function contactName(detail: {
  identities: { displayName: string | null; handle: string | null }[];
}) {
  return (
    detail.identities[0]?.displayName ??
    detail.identities[0]?.handle ??
    "Contact non rapproché"
  );
}

function Row({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-encre-400">{label}</dt>
      <dd className={alert ? "font-medium text-grenat-700" : "text-encre-800"}>{value}</dd>
    </div>
  );
}

/**
 * Corps d'une bulle, selon la forme normalisée du message.
 *
 * L'ancienne version n'affichait que `payload.text` et retombait sur
 * `[${kind}]` pour tout le reste — soit un gabarit de débogage montré à
 * l'équipe. Or sur Instagram, canal n°1 du salon, les formes SANS texte sont
 * les plus utiles : une photo d'inspiration et un post partagé valent dix
 * messages écrits (« je veux CE nail art »). Les afficher `[image]` revenait
 * à jeter l'information la plus précieuse du fil.
 *
 * Règle : aucune branche ne retombe sur le nom technique du `kind`. Un canal
 * inconnu affiche un libellé lisible, jamais du jargon interne.
 */
function MessageBody({ payload, outbound }: { payload: MessagePayload; outbound: boolean }) {
  // Les libellés secondaires doivent rester lisibles sur fond blush (sortant)
  // comme sur fond crème (entrant).
  const muted = outbound ? "text-white/70" : "text-encre-500";
  const body = "whitespace-pre-wrap break-words";

  switch (payload.kind) {
    case "text":
      return <p className={body}>{payload.text}</p>;

    case "image":
      return (
        <figure>
          <img
            src={payload.url}
            alt={payload.caption ?? "Photo envoyée par la cliente"}
            className="max-h-64 w-full rounded-lg object-cover"
            loading="lazy"
          />
          {payload.caption && <figcaption className={`mt-1 ${body}`}>{payload.caption}</figcaption>}
        </figure>
      );

    case "video":
      return (
        <div>
          <video src={payload.url} controls className="max-h-64 w-full rounded-lg" />
          {payload.caption && <p className={`mt-1 ${body}`}>{payload.caption}</p>}
        </div>
      );

    case "audio":
      return (
        <div>
          <p className={`mb-1 text-xs ${muted}`}>🎤 Message vocal</p>
          <audio src={payload.url} controls className="w-full" />
          {payload.transcript && <p className={`mt-1 ${body}`}>{payload.transcript}</p>}
        </div>
      );

    case "file":
      return (
        <a href={payload.url} target="_blank" rel="noreferrer" className="underline">
          📎 {payload.filename}
        </a>
      );

    /* Répondre à une story sans voir LAQUELLE, c'est répondre à l'aveugle :
       la vignette porte ici tout le contexte. */
    case "story_reply":
      return (
        <div>
          <p className={`mb-1 text-xs ${muted}`}>↩️ Réponse à une story</p>
          {payload.storyUrl && (
            <img
              src={payload.storyUrl}
              alt="Story"
              className="mb-1 max-h-40 rounded-lg object-cover"
              loading="lazy"
            />
          )}
          {payload.text && <p className={body}>{payload.text}</p>}
        </div>
      );

    case "story_mention":
      return (
        <div>
          <p className={`mb-1 text-xs ${muted}`}>↩️ Mention en story</p>
          {payload.storyUrl && (
            <img
              src={payload.storyUrl}
              alt="Story"
              className="max-h-40 rounded-lg object-cover"
              loading="lazy"
            />
          )}
        </div>
      );

    case "post_share":
      return (
        <a href={payload.permalink} target="_blank" rel="noreferrer" className="block">
          {payload.thumbnailUrl && (
            <img
              src={payload.thumbnailUrl}
              alt="Publication partagée"
              className="mb-1 max-h-64 w-full rounded-lg object-cover"
              loading="lazy"
            />
          )}
          <span className="underline">🔗 Publication partagée</span>
        </a>
      );

    case "reaction":
      return <p className="text-2xl leading-none">{payload.emoji}</p>;

    /* Un avis se juge d'un coup d'œil : la note d'abord, le texte ensuite.
       Une étoile sans commentaire reste une information — et une urgence. */
    case "review":
      return (
        <div>
          <p className="mb-1 flex items-center gap-2">
            <span
              aria-label={`${payload.rating} sur 5`}
              className={payload.rating <= 2 ? "text-grenat-700" : "text-ambre-700"}
            >
              {"★".repeat(payload.rating)}
              <span className={muted}>{"☆".repeat(Math.max(0, 5 - payload.rating))}</span>
            </span>
            {payload.authorName && (
              <span className={`text-xs ${muted}`}>{payload.authorName}</span>
            )}
          </p>
          {payload.text ? (
            <p className={body}>{payload.text}</p>
          ) : (
            <p className={`italic ${muted}`}>Note laissée sans commentaire</p>
          )}
        </div>
      );

    case "booking_card":
      return <p className={body}>📅 Rendez-vous</p>;

    case "system":
      return <p className={`italic ${body}`}>{payload.event}</p>;

    default:
      // Forme ajoutée côté API mais pas encore rendue ici : on reste lisible.
      return <p className={`italic ${muted}`}>Message non pris en charge</p>;
  }
}

/** Employées présentables au formulaire de RDV. */
function staffOptions(team?: Awaited<ReturnType<typeof api.admin.team.list.query>>) {
  return (team ?? [])
    .filter((member) => member.displayName)
    .map((member) => ({
      id: member.id,
      displayName: member.displayName!,
      calendarColor: member.calendarColor ?? "#c98a9b",
    }));
}

/**
 * Reconstruit la forme attendue par le formulaire à partir du fil.
 *
 * `inbox.thread` ne renvoie pas la cliente imbriquée dans chaque rendez-vous —
 * elle est déjà à la racine de la conversation. On la recolle ici plutôt que
 * d'alourdir la requête pour un cas qui ne concerne qu'un bouton.
 */
function editableFrom(
  detail: Awaited<ReturnType<typeof api.inbox.thread.query>>,
  appointmentId: string,
): EditableAppointment | undefined {
  const appointment = detail.appointments.find((row) => row.id === appointmentId);
  if (!appointment) return undefined;

  const client = detail.conversation.client;
  return {
    id: appointment.id,
    staffId: appointment.staffId,
    blockStartsAt: appointment.blockStartsAt,
    staffNote: appointment.staffNote,
    guestName: appointment.guestName,
    client: client
      ? {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
          phone: client.phone,
          clientProfile: client.clientProfile
            ? { allergies: client.clientProfile.allergies }
            : null,
        }
      : null,
    lines: appointment.lines.map((line) => ({ serviceId: line.serviceId })),
  };
}
